import React, { useEffect, useRef, useState } from "react";
import { RefreshCcw, Send, Sparkles, X } from "lucide-react";

// Shared UI primitives for the C-ORE Intelligence modules.
// Mirrors the conventions used in CFO/CEO/CMO/CTO pages.

export const STATUS = {
  green: { bar: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700", left: "border-l-4 border-emerald-500", soft: "bg-emerald-50/70", border: "border-emerald-300" },
  amber: { bar: "bg-amber-500",   text: "text-amber-700",   chip: "bg-amber-100 text-amber-700",     left: "border-l-4 border-amber-500",   soft: "bg-amber-50/70",   border: "border-amber-300"   },
  red:   { bar: "bg-red-500",     text: "text-red-700",     chip: "bg-red-100 text-red-700",         left: "border-l-4 border-red-500",     soft: "bg-red-50/70",     border: "border-red-300"     },
  blue:  { bar: "bg-blue-500",    text: "text-blue-700",    chip: "bg-blue-100 text-blue-700",       left: "border-l-4 border-blue-500",    soft: "bg-blue-50/70",    border: "border-blue-300"    },
  grey:  { bar: "bg-slate-400",   text: "text-slate-600",   chip: "bg-slate-100 text-slate-700",     left: "border-l-4 border-slate-300",   soft: "bg-slate-50",      border: "border-slate-300"   },
};

export function Section({ title, subtitle, right, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 tablet:p-5 desktop:p-5 shadow-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">{title}</div>
          {subtitle && <div className="text-[12px] text-slate-500">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="mt-4 core-scroll-x">{children}</div>
    </section>
  );
}

export function Modal({ open, onClose, title, children, testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="text-[14px] font-bold text-[#1E3A5F]">{title}</div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="rounded p-1 text-slate-400 hover:text-[#0F1B2D]"><X size={16} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

import KpiValue from "../KpiValue";
import EditableValue from "../EditableValue";

export function KpiCard({ kpi, testidPrefix, isLoading = false, dataAvailable = false, realValue = null, realText = null,
                          editable = null, customerId = null, rawValue = null, displayValue = undefined,
                          onSaved = null, lastUpdatedIso = null, forceSub = false }) {
  const s = STATUS[kpi.status] || STATUS.blue;
  const Icon = kpi.icon;
  const isEditable = !!(editable && customerId);
  // Real-data only. Active customers get auto-dashing via KpiValue when
  // a metric isn't yet wired or hasn't been ingested. When `editable` is
  // passed (e.g. Company Trajectory's "Today" baseline), the customer's own
  // manually-entered figure always wins over that dash/live-data logic.
  return (
    <div data-testid={`${testidPrefix}-${kpi.id}`} className="relative overflow-hidden rounded-lg border border-[#DBEAFE] bg-white p-4 shadow-card transition-transform hover:-translate-y-[1px]">
      <div className={`absolute inset-x-0 top-0 h-1 ${s.bar}`} />
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">{Icon && <Icon size={13} className={s.text} />}{kpi.label}</span>
      </div>
      <div className="mt-2">
        {isEditable ? (
          <EditableValue
            testId={`${testidPrefix}-${kpi.id}-edit`}
            displayValue={displayValue ?? "—"}
            rawValue={rawValue}
            paramKey={editable.paramKey}
            paramLabel={kpi.label}
            customerId={customerId}
            transformIn={(v) => v ?? ""}
            transformOut={(v) => v === "" ? null : Math.round(Number(v) * 1_00_00_000)}
            lastUpdatedIso={lastUpdatedIso}
            onSaved={onSaved}
            inputPlaceholder={editable.placeholder}
          />
        ) : (
          <KpiValue
            realValue={realValue}
            realText={realText}
            format={kpi.format || "currency"}
            label={kpi.label}
            tooltip={kpi.tooltip || "Connect Tally and complete the relevant integration to see this metric."}
            size="large"
            isLoading={isLoading}
            dataAvailable={dataAvailable}
          />
        )}
      </div>
      {kpi.sub && (
        <div className="mt-1 text-[11.5px] text-slate-500">
          {isEditable || forceSub ? kpi.sub : (dataAvailable && (realValue != null || realText != null) ? kpi.sub : "Live once data is wired")}
        </div>
      )}
    </div>
  );
}

export function InsightCard({ insight, testidPrefix }) {
  const s = STATUS[insight.severity] || STATUS.blue;
  const conf = Number.isFinite(Number(insight.confidence)) ? Number(insight.confidence) : null;
  const confTone = conf === null ? "" : conf >= 75 ? "bg-emerald-100 text-emerald-700" : conf >= 40 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";
  const isConnectorPrompt = (insight.source || "").toLowerCase().includes("connect tally");
  return (
    <div data-testid={`${testidPrefix}-${insight.id}`} className={`rounded-lg ${s.left} ${s.soft} p-4`}>
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-[13.5px] font-bold text-[#0F1B2D]">{insight.title}</h4>
        {conf !== null && (
          <span data-testid={`${testidPrefix}-${insight.id}-confidence`}
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${confTone}`}
            title={`Confidence: ${conf}% — based on the completeness of the source data`}>
            {conf}%
          </span>
        )}
      </div>
      <p className="mt-1 text-[12.5px] leading-snug text-slate-700">{insight.body}</p>
      <p className="mt-2 text-[12px] font-semibold text-emerald-700">{insight.action}</p>
      {insight.source && (
        <div className="mt-2 flex items-center gap-1 text-[10.5px] text-slate-500">
          <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">Source</span>
          <span className={isConnectorPrompt ? "font-semibold text-amber-700" : ""}>{insight.source}</span>
        </div>
      )}
    </div>
  );
}

export function InsightsGrid({ subtitle, fetchFn, testidPrefix, dataAvailable = true }) {
  // Real-data only. We start with an empty list and only render real
  // engine / LLM-generated cards once they arrive — never any hardcoded
  // fallback content.
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("loading");
  const [loading, setLoading] = useState(false);
  const [moduleConfidence, setModuleConfidence] = useState(null);
  const [dataSources, setDataSources] = useState([]);

  const refresh = async () => {
    setLoading(true);
    const res = await fetchFn();
    setLoading(false);
    if (res.ok) {
      const conf = Number.isFinite(Number(res.data?.confidence)) ? Number(res.data.confidence) : null;
      const srcs = Array.isArray(res.data?.data_sources) ? res.data.data_sources : [];
      setModuleConfidence(conf);
      setDataSources(srcs);
    }
    if (res.ok && res.data?.source === "ai" && Array.isArray(res.data.items) && res.data.items.length >= 3) {
      setItems(res.data.items);
      setSource("ai");
    } else {
      setItems([]);
      setSource("empty");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [dataAvailable]);

  return (
    <section data-testid={`${testidPrefix}-insights`} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Executive Insights</div>
          <div className="text-[13px] text-slate-500">
            {subtitle}
            {source === "ai" && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700">C-ORE generated</span>}
            {moduleConfidence !== null && (
              <span data-testid={`${testidPrefix}-insights-confidence`}
                className={`ml-2 rounded-full px-2 py-0.5 text-[10.5px] font-medium ${moduleConfidence >= 75 ? "bg-emerald-100 text-emerald-700" : moduleConfidence >= 40 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}
                title="How complete the source data is for this module">
                Data confidence {moduleConfidence}%
              </span>
            )}
            {dataSources.length > 0 && (
              <span data-testid={`${testidPrefix}-insights-sources`} className="ml-2 text-[10.5px] text-slate-500">
                · {dataSources.join(" + ")}
              </span>
            )}
          </div>
        </div>
        <button data-testid={`${testidPrefix}-insights-refresh`} onClick={refresh} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11.5px] font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50">
          <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "Generating…" : "Refresh insights"}
        </button>
      </div>
      {items.length === 0 ? (
        <div
          data-testid={`${testidPrefix}-insights-empty`}
          className="rounded-xl border border-dashed border-blue-200 bg-blue-50/50 px-4 py-8 text-center text-[12.5px] text-blue-700"
        >
          {loading
            ? "Generating insights from your latest Tally data…"
            : "Connect Tally and run a sync to unlock personalised strategic insights for this module."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-2">
          {items.map((it) => <InsightCard key={it.id} insight={it} testidPrefix={`${testidPrefix}-insight`} />)}
        </div>
      )}
    </section>
  );
}

function Typing({ testid }) {
  return (
    <div data-testid={testid} className="max-w-[85%] rounded-2xl bg-[#EFF6FF] px-4 py-3">
      <div className="flex items-center gap-1">
        {[0, 200, 400].map((d) => (
          <span key={d} className="h-1.5 w-1.5 rounded-full bg-slate-400 core-pulse" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
    </div>
  );
}

export function ChatPanel({ title, subtitle, placeholder, sendFn, testidPrefix, emptyHint }) {
  // Real-data only — chat always starts empty. No preload, no demo
  // conversation seeding.
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sid = useRef(`${testidPrefix}-${Math.random().toString(36).slice(2, 10)}`);
  const scroll = useRef(null);

  useEffect(() => { if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [messages, sending]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);
    const res = await sendFn(t, sid.current);
    const reply = res.ok
      ? (res.data?.reply || "Sorry, I could not process that right now. Please try again in a moment.")
      : (res.error || "Sorry, I could not process that right now. Please try again in a moment.");
    setMessages((m) => [...m, { role: "ai", text: reply }]);
    setSending(false);
  };

  return (
    <section data-testid={`${testidPrefix}-chat`} className="rounded-xl border border-slate-200 bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">{title}</div>
          <div className="text-[12px] text-slate-500">{subtitle}</div>
        </div>
        <Sparkles size={16} className="text-emerald-600" />
      </div>
      <div ref={scroll} className="space-y-2 overflow-y-auto px-5 py-3" style={{ maxHeight: 280 }}>
        {messages.length === 0 && !sending && (
          <div data-testid={`${testidPrefix}-chat-empty`} className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            {emptyHint || `Ask anything in ${placeholder ? `English or Hindi — ${placeholder.toLowerCase()}` : "English or Hindi."}`}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`${testidPrefix}-chat-msg-${i}`}
            className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] leading-snug ${m.role === "ai" ? "bg-[#EFF6FF] text-[#0F1B2D]" : "ml-auto bg-emerald-600 text-white"}`}>
            {m.text}
          </div>
        ))}
        {sending && <Typing testid={`${testidPrefix}-chat-typing`} />}
      </div>
      <div className="border-t border-slate-100 px-5 py-3">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
          <input data-testid={`${testidPrefix}-chat-input`} type="text" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder={sending ? "Thinking…" : placeholder}
            disabled={sending}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-[#0F1B2D] placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
          />
          <button type="submit" data-testid={`${testidPrefix}-chat-send`} disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={13} /> Send
          </button>
        </form>
      </div>
    </section>
  );
}

export function ModuleHeader({ overline, title, right, testid = "module-header" }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-2" data-testid={testid}>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">{overline}</div>
        <h2 className="font-display text-[20px] font-bold text-blue-800">{title}</h2>
      </div>
      {right && <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-500">{right}</div>}
    </div>
  );
}
